import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  GangCreationFormData,
  GangCreationStep,
  GangParticipant,
  GangParticipantWithRole,
} from '../types/gangCreationTypes';

const emptyFormData: GangCreationFormData = {
  name: '',
  isEqualSplit: true, // Default to equal split for new gangs
  participants: {},
  leaderIds: [],
  memberIds: [],
};

const cloneParticipants = (
  participants: Record<string, GangParticipant>
): Record<string, GangParticipant> =>
  Object.keys(participants).reduce<Record<string, GangParticipant>>(
    (accumulator, userId) => {
      accumulator[userId] = { ...participants[userId] };
      return accumulator;
    },
    {}
  );

const sortIds = (ids: string[]): string[] => [...ids].sort();

const arraysMatch = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;

  const sortedA = sortIds(a);
  const sortedB = sortIds(b);

  for (let index = 0; index < sortedA.length; index += 1) {
    if (sortedA[index] !== sortedB[index]) {
      return false;
    }
  }

  return true;
};

const participantsEqual = (
  current?: GangParticipant,
  initial?: GangParticipant
): boolean => {
  if (!current && !initial) {
    return true;
  }

  if (!current || !initial) {
    return false;
  }

  return (
    current.fullName === initial.fullName &&
    current.email === initial.email &&
    current.canSeeSplit === initial.canSeeSplit &&
    current.paymentType === initial.paymentType &&
    (current.defaultSplitPercentage ?? null) ===
      (initial.defaultSplitPercentage ?? null) &&
    (current.defaultRate ?? null) === (initial.defaultRate ?? null) &&
    (current.defaultUnitsValue ?? null) === (initial.defaultUnitsValue ?? null)
  );
};

const collectParticipantIds = (data: GangCreationFormData) => [
  ...data.leaderIds,
  ...data.memberIds,
];

const collectParticipantsWithRoles = (
  data: GangCreationFormData
): GangParticipantWithRole[] => {
  const participants: GangParticipantWithRole[] = [];

  data.leaderIds.forEach(userId => {
    const participant = data.participants[userId];
    if (participant) {
      participants.push({ ...participant, role: 'leader' });
    }
  });

  data.memberIds.forEach(userId => {
    const participant = data.participants[userId];
    if (participant) {
      participants.push({ ...participant, role: 'member' });
    }
  });

  return participants;
};

const haveMembershipChanged = (
  currentData: GangCreationFormData,
  initialData: GangCreationFormData | null
): boolean => {
  if (!initialData) {
    return true;
  }

  if (!arraysMatch(currentData.leaderIds, initialData.leaderIds)) {
    return true;
  }

  if (!arraysMatch(currentData.memberIds, initialData.memberIds)) {
    return true;
  }

  const uniqueIds = new Set([
    ...collectParticipantIds(currentData),
    ...collectParticipantIds(initialData),
  ]);

  for (const userId of uniqueIds) {
    const currentParticipant = currentData.participants[userId];
    const initialParticipant = initialData.participants[userId];

    if (!participantsEqual(currentParticipant, initialParticipant)) {
      return true;
    }
  }

  return false;
};

export const useGangLifecycleForm = (
  currentStep: GangCreationStep,
  initial: GangCreationFormData | null
) => {
  const form = useForm<GangCreationFormData>({
    defaultValues: initial
      ? { ...initial, participants: cloneParticipants(initial.participants) }
      : emptyFormData,
    mode: 'onChange',
  });

  const initialRef = useRef<GangCreationFormData | null>(
    initial
      ? { ...initial, participants: cloneParticipants(initial.participants) }
      : null
  );

  useEffect(() => {
    if (initial) {
      form.reset({
        ...initial,
        participants: cloneParticipants(initial.participants),
      });
      initialRef.current = {
        ...initial,
        participants: cloneParticipants(initial.participants),
      };
    } else {
      form.reset(emptyFormData);
      initialRef.current = null;
    }
  }, [initial, form]);

  const isStepValid = (step: GangCreationStep): boolean => {
    const data = form.getValues();

    switch (step) {
      case GangCreationStep.NAME:
        return !!data.name?.trim() && data.name.trim().length >= 2;
      case GangCreationStep.LEADERS:
        return data.leaderIds.length > 0;
      case GangCreationStep.MEMBERS:
        return true;
      case GangCreationStep.MEMBER_CONFIG: {
        const allParticipants = collectParticipantsWithRoles(data);
        return allParticipants.every(
          participant =>
            participant.userId && participant.fullName && participant.email
        );
      }
      case GangCreationStep.SUMMARY: {
        const totalMembers = data.leaderIds.length + data.memberIds.length;
        const hasAtLeastOneLeader = data.leaderIds.length >= 1;
        const hasAtLeastTwoMembers = totalMembers >= 2;
        return hasAtLeastOneLeader && hasAtLeastTwoMembers;
      }
      default:
        return false;
    }
  };

  const isCurrentStepValid = (): boolean => isStepValid(currentStep);

  const getStepData = (step: GangCreationStep) => {
    const data = form.getValues();

    switch (step) {
      case GangCreationStep.NAME:
        return { name: data.name };
      case GangCreationStep.LEADERS:
        return { leaderIds: data.leaderIds };
      case GangCreationStep.MEMBERS:
        return { memberIds: data.memberIds };
      case GangCreationStep.MEMBER_CONFIG:
        return {
          leaderIds: data.leaderIds,
          memberIds: data.memberIds,
          participants: data.participants,
        };
      case GangCreationStep.SUMMARY:
        return data;
      default:
        return {};
    }
  };

  const hasChanges = (): boolean => {
    const currentData = form.getValues();
    const initialData = initialRef.current;

    const nameChanged =
      (initialData?.name?.trim() ?? '') !== (currentData.name?.trim() ?? '');

    const isEqualSplitChanged =
      (initialData?.isEqualSplit ?? true) !==
      (currentData.isEqualSplit ?? true);

    const membershipChanged = haveMembershipChanged(currentData, initialData);

    return nameChanged || isEqualSplitChanged || membershipChanged;
  };

  const hasOnlyNameChanged = (): boolean => {
    const currentData = form.getValues();
    const initialData = initialRef.current;

    const nameChanged =
      (initialData?.name?.trim() ?? '') !== (currentData.name?.trim() ?? '');

    const isEqualSplitChanged =
      (initialData?.isEqualSplit ?? true) !==
      (currentData.isEqualSplit ?? true);

    const membershipChanged = haveMembershipChanged(currentData, initialData);

    return nameChanged && !isEqualSplitChanged && !membershipChanged;
  };

  const getLeaderParticipants = (): GangParticipantWithRole[] => {
    const data = form.getValues();
    return data.leaderIds
      .map(userId => data.participants[userId])
      .filter((participant): participant is GangParticipant =>
        Boolean(participant)
      )
      .map(participant => ({ ...participant, role: 'leader' as const }));
  };

  const getMemberParticipants = (): GangParticipantWithRole[] => {
    const data = form.getValues();
    return data.memberIds
      .map(userId => data.participants[userId])
      .filter((participant): participant is GangParticipant =>
        Boolean(participant)
      )
      .map(participant => ({ ...participant, role: 'member' as const }));
  };

  return {
    form,
    isStepValid,
    isCurrentStepValid,
    getStepData,
    hasChanges,
    hasOnlyNameChanged,
    getLeaderParticipants,
    getMemberParticipants,
  };
};
